import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMembers,
  createMember,
  updateMember,
  deleteMember,
  getStudyAssociations,
  uploadMemberLogo,
  deleteMemberLogo,
  type Member,
  type StudyAssociation
} from '../lib/admin';
import { getUser } from '../lib/auth';
import { Building2, Edit, Trash2, Plus, Users, UserCog, Brain, CheckCircle, XCircle, Image, GraduationCap } from 'lucide-react';
import LogoUpload from './LogoUpload';
import { useToast, useConfirm } from './ui';

export default function MemberManagement() {
  const currentUser = getUser();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [members, setMembers] = useState<Member[]>([]);
  const [studyAssociations, setStudyAssociations] = useState<StudyAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterStudyAssociation, setFilterStudyAssociation] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    organization_name: '',
    organization_number: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    study_association_id: '',
    status: 'active'
  });

  useEffect(() => {
    loadData();
  }, [filterStudyAssociation]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const associationsData = await getStudyAssociations();
      setStudyAssociations(associationsData);

      // Load members, optionally filtered by study association
      const membersData = await getMembers(filterStudyAssociation || undefined);
      setMembers(membersData);

    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste medlemmer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.organization_name || !formData.study_association_id) {
      toast.showWarning('Organisasjonsnavn og studieforbund er påkrevd');
      return;
    }

    try {
      setSaving(true);
      await createMember(formData);
      toast.showSuccess('Medlemsorganisasjon opprettet!');
      setShowCreateForm(false);
      resetForm();
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved opprettelse: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingMember) return;

    try {
      setSaving(true);
      await updateMember(editingMember.id, formData);

      // Clear filter if study association was changed
      if (formData.study_association_id !== editingMember.study_association_id) {
        setFilterStudyAssociation('');
      }

      toast.showSuccess('Medlemsorganisasjon oppdatert!');
      setEditingMember(null);
      resetForm();
      document.body.style.overflow = 'unset';
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved oppdatering: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (member: Member) => {
    if (currentUser.role !== 'systemadmin') {
      toast.showWarning('Kun systemadmin kan deaktivere medlemsorganisasjoner');
      return;
    }

    const confirmed = await confirm({
      title: 'Deaktiver medlemsorganisasjon',
      message: `Er du sikker på at du vil deaktivere ${member.organization_name}?\n\nAlle tilknyttede brukere og data vil beholdes, men organisasjonen vil ikke være aktiv.`,
      confirmLabel: 'Deaktiver',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteMember(member.id);
      toast.showSuccess(`${member.organization_name} er deaktivert`);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved deaktivering: ${err.message}`);
    }
  };

  const startEdit = (member: Member) => {
    setEditingMember(member);
    setFormData({
      organization_name: member.organization_name,
      organization_number: member.organization_number || '',
      contact_email: member.contact_email || '',
      contact_phone: member.contact_phone || '',
      address: member.address || '',
      study_association_id: member.study_association_id,
      status: member.status
    });
    setShowCreateForm(false);

    // Lock body scroll when modal opens
    document.body.style.overflow = 'hidden';
  };

  const resetForm = () => {
    setFormData({
      organization_name: '',
      organization_number: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      study_association_id: currentUser.role === 'admin' ? currentUser.study_association_id : '',
      status: 'active'
    });
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setShowCreateForm(false);
    resetForm();
    document.body.style.overflow = 'unset';
  };

  const navigateToSensors = (memberId: string) => {
    navigate(`/members/${memberId}/sensors`);
  };

  const navigateToUsers = (memberId: string) => {
    navigate(`/members?tab=users&member_id=${memberId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B5651D] mx-auto mb-4"></div>
          <p className="text-gray-600">Laster medlemmer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-[#B5651D]" />
            <h1 className="text-3xl font-bold text-gray-900">Medlemsorganisasjoner</h1>
          </div>
          {(currentUser.role === 'systemadmin' || currentUser.role === 'admin') && (
            <button
              onClick={() => {
                setShowCreateForm(true);
                setEditingMember(null);
                resetForm();
              }}
              className="flex items-center space-x-2 bg-[#B5651D] text-white px-4 py-2 rounded-lg hover:bg-[#8B4513] transition"
            >
              <Plus className="w-5 h-5" />
              <span>Ny Organisasjon</span>
            </button>
          )}
        </div>
        <p className="text-gray-600">
          Administrer medlemsorganisasjoner tilknyttet studieforbund
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Filter Section */}
      {currentUser.role === 'systemadmin' && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Filtrer på studieforbund:</label>
            <select
              value={filterStudyAssociation}
              onChange={(e) => setFilterStudyAssociation(e.target.value)}
              className="flex-1 max-w-md rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
            >
              <option value="">Alle studieforbund</option>
              {studyAssociations.map(sa => (
                <option key={sa.id} value={sa.id}>{sa.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Create Form (inline) */}
      {showCreateForm && !editingMember && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Opprett Ny Organisasjon
          </h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisasjonsnavn *
                </label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisasjonsnummer
                </label>
                <input
                  type="text"
                  value={formData.organization_number}
                  onChange={(e) => setFormData({ ...formData, organization_number: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Studieforbund *
                </label>
                <select
                  value={formData.study_association_id}
                  onChange={(e) => setFormData({ ...formData, study_association_id: e.target.value })}
                  required
                  disabled={currentUser.role === 'admin'}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                >
                  <option value="">Velg studieforbund</option>
                  {studyAssociations.map(sa => (
                    <option key={sa.id} value={sa.id}>{sa.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kontakt E-post
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kontakt Telefon
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={2}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
              >
                {saving ? 'Lagrer...' : 'Opprett'}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Rediger Organisasjon
            </h2>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organisasjonsnavn *
                  </label>
                  <input
                    type="text"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    required
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organisasjonsnummer
                  </label>
                  <input
                    type="text"
                    value={formData.organization_number}
                    onChange={(e) => setFormData({ ...formData, organization_number: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Studieforbund *
                  </label>
                  <select
                    value={formData.study_association_id}
                    onChange={(e) => setFormData({ ...formData, study_association_id: e.target.value })}
                    required
                    disabled={currentUser.role === 'admin'}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  >
                    <option value="">Velg studieforbund</option>
                    {studyAssociations.map(sa => (
                      <option key={sa.id} value={sa.id}>{sa.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kontakt E-post
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kontakt Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                </div>

                {currentUser.role === 'systemadmin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                    >
                      <option value="active">Aktiv</option>
                      <option value="inactive">Inaktiv</option>
                      <option value="suspended">Suspendert</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              {/* Logo Upload */}
              <LogoUpload
                currentLogoPath={editingMember.logo_path}
                onUpload={async (file) => {
                  const result = await uploadMemberLogo(editingMember.id, file);
                  if (result.success) {
                    setEditingMember({
                      ...editingMember,
                      logo_path: result.logo_path || null
                    });
                    loadData();
                  }
                  return result;
                }}
                onDelete={async () => {
                  await deleteMemberLogo(editingMember.id);
                  setEditingMember({
                    ...editingMember,
                    logo_path: null
                  });
                  loadData();
                }}
                disabled={saving}
              />

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
                >
                  {saving ? 'Lagrer...' : 'Oppdater'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organisasjon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Studieforbund
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kontakt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statistikk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    Ingen medlemsorganisasjoner funnet
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {member.logo_path ? (
                          <img
                            src={`${import.meta.env.VITE_API_URL || ''}${member.logo_path}`}
                            alt={member.organization_name}
                            className="h-8 w-8 object-contain flex-shrink-0"
                          />
                        ) : (
                          <Building2 className="h-8 w-8 text-gray-400 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {member.organization_name}
                            {member.logo_path && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <Image size={10} className="mr-0.5" />
                                Logo
                              </span>
                            )}
                          </div>
                          {member.organization_number && (
                            <div className="text-sm text-gray-500">
                              Org.nr: {member.organization_number}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {member.study_association_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{member.contact_email || 'Ingen e-post'}</div>
                      <div className="text-sm text-gray-500">{member.contact_phone || 'Ingen telefon'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <Users className="w-4 h-4 mr-1" />
                          <span>{member.user_count || 0} brukere</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="w-4 h-4 mr-1" />
                          <span>{member.sensor_count || 0} sensorer</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Brain className="w-4 h-4 mr-1" />
                          <span>{member.ai_requests_count} AI-forespørsler</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        member.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : member.status === 'suspended'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {member.status === 'active' ? (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Aktiv
                          </>
                        ) : member.status === 'suspended' ? (
                          'Suspendert'
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Inaktiv
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center space-x-2">
                        {(currentUser.role === 'systemadmin' || currentUser.role === 'admin') && (
                          <button
                            onClick={() => navigateToUsers(member.id)}
                            className="text-blue-600 hover:text-blue-800 transition"
                            title="Vis brukere"
                          >
                            <UserCog className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => navigateToSensors(member.id)}
                          className="text-[#B5651D] hover:text-[#8B4513] transition"
                          title="Administrer sensorer"
                        >
                          <Users className="w-5 h-5" />
                        </button>
                        {(currentUser.role === 'systemadmin' || currentUser.role === 'admin') && (
                          <>
                            <button
                              onClick={() => startEdit(member)}
                              className="text-green-600 hover:text-green-800 transition"
                              title="Rediger organisasjon"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            {currentUser.role === 'systemadmin' && member.status === 'active' && (
                              <button
                                onClick={() => handleDelete(member)}
                                className="text-red-600 hover:text-red-800 transition"
                                title="Deaktiver"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      {members.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Medlemmer</p>
                <p className="text-2xl font-bold text-gray-900">{members.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-[#B5651D]" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Brukere</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.reduce((sum, m) => sum + Number(m.user_count || 0), 0)}
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Sensorer</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.reduce((sum, m) => sum + Number(m.sensor_count || 0), 0)}
                </p>
              </div>
              <GraduationCap className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt AI-forespørsler</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.reduce((sum, m) => sum + Number(m.ai_requests_count || 0), 0)}
                </p>
              </div>
              <Brain className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
